import { RefObject, useEffect, useRef, useState } from 'react';

import { FRAME_COUNT, getFrameUrl } from 'constants/dnaHub';
import { useScrollProgress } from 'hooks/useScrollProgress';
import { DnaFrameState, PreloadedFrame } from 'types/dnaHub';
import { computeDnaFrameState, dnaFrameStatesEqual } from 'utils/computeDnaFrameState';
import { preloadFrames } from 'utils/preloadFrames';

export interface DnaHubDebugSnapshot {
  framesLoaded: number;
  totalFrames: number;
  decodeTimeMs: number;
  frameIndex: number;
  fps: number;
}

export interface UseFrameSequenceResult {
  canvasRef: RefObject<HTMLCanvasElement>;
  trackRef: RefObject<HTMLDivElement>;
  stageRef: RefObject<HTMLDivElement>;
  headlineRef: RefObject<HTMLDivElement>;
  debugSnapshot: DnaHubDebugSnapshot | null;
}

const MAX_DEVICE_PIXEL_RATIO = 2;
const FPS_SAMPLE_INTERVAL_MS = 500;

const emptyFrameState: DnaFrameState = {
  headOpacity: -1,
  dnaOpacity: -1,
  frameIndex: -1,
  x: -1,
  y: -1,
  width: -1,
  height: -1,
};

export const useFrameSequence = (debugEnabled: boolean): UseFrameSequenceResult => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const getProgress = useScrollProgress(trackRef, stageRef);

  const [debugSnapshot, setDebugSnapshot] = useState<DnaHubDebugSnapshot | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    const track = trackRef.current;
    const headline = headlineRef.current;
    if (!canvas || !stage || !track || !headline) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let frames: PreloadedFrame[] = [];
    let decodeTimeMs = 0;
    let rafId = 0;
    let cancelled = false;
    let lastState = emptyFrameState;

    let fpsFrameCount = 0;
    let fpsWindowStart = performance.now();

    const sizeCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
      const width = stage.clientWidth;
      const height = stage.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    };

    const draw = () => {
      const p = getProgress();
      const width = stage.clientWidth;
      const height = stage.clientHeight;
      const state = computeDnaFrameState(p, width, height);

      headline.style.opacity = state.headOpacity.toFixed(3);

      if (!dnaFrameStatesEqual(state, lastState)) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        const frame = frames[state.frameIndex];
        if (frame && state.dnaOpacity > 0) {
          ctx.globalAlpha = state.dnaOpacity;
          ctx.drawImage(frame, state.x, state.y, state.width, state.height);
          ctx.globalAlpha = 1;
        }
        lastState = state;
      }

      if (debugEnabled) {
        fpsFrameCount += 1;
        const elapsed = performance.now() - fpsWindowStart;
        if (elapsed >= FPS_SAMPLE_INTERVAL_MS) {
          setDebugSnapshot({
            framesLoaded: frames.length,
            totalFrames: FRAME_COUNT,
            decodeTimeMs,
            frameIndex: state.frameIndex,
            fps: Math.round((fpsFrameCount * 1000) / elapsed),
          });
          fpsFrameCount = 0;
          fpsWindowStart = performance.now();
        }
      }
    };

    const tick = () => {
      draw();
      rafId = requestAnimationFrame(tick);
    };

    sizeCanvas();
    window.addEventListener('resize', sizeCanvas);

    preloadFrames(FRAME_COUNT, getFrameUrl, (loadedCount) => {
      if (debugEnabled) {
        setDebugSnapshot((prev) => ({
          framesLoaded: loadedCount,
          totalFrames: FRAME_COUNT,
          decodeTimeMs: prev?.decodeTimeMs ?? 0,
          frameIndex: prev?.frameIndex ?? 0,
          fps: prev?.fps ?? 0,
        }));
      }
    }).then((result) => {
      if (cancelled) return;
      frames = result.frames;
      decodeTimeMs = result.totalDecodeTimeMs;
      tick();
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', sizeCanvas);
    };
  }, [debugEnabled, getProgress]);

  return { canvasRef, trackRef, stageRef, headlineRef, debugSnapshot };
};
